import { z } from 'zod';
import { ApiKeyStatus } from '../enums';

/**
 * API Key DTO Schema
 */
export const ApiKeySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string().min(1).max(255),
  key: z.string(), // Hashed key stored in DB
  prefix: z.string().length(8), // First 8 chars for display
  status: z.nativeEnum(ApiKeyStatus).default(ApiKeyStatus.ACTIVE),
  scopes: z.array(z.string()).default([]),
  lastUsedAt: z.date().nullable().optional(),
  expiresAt: z.date().nullable().optional(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ApiKey = z.infer<typeof ApiKeySchema>;

/**
 * Create API Key DTO Schema (for API requests)
 * businessId and createdBy are optional in the request body as they are extracted from JWT
 */
export const CreateApiKeySchema = z.object({
  businessId: z.string().uuid().optional(),
  name: z.string().min(1).max(255),
  scopes: z.array(z.string()).min(1, 'At least one scope is required'),
  scopePreset: z.enum(['read_only', 'standard', 'full_access', 'custom']).optional(),
  expiresAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().uuid().optional(),
});

export type CreateApiKey = z.infer<typeof CreateApiKeySchema>;

/**
 * API Key Response DTO Schema (includes plain text key only on creation)
 */
export const ApiKeyResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  prefix: z.string(),
  status: z.nativeEnum(ApiKeyStatus),
  scopes: z.array(z.string()),
  key: z.string().optional(), // Only included on creation
  expiresAt: z.date().nullable().optional(),
  createdAt: z.date(),
});

export type ApiKeyResponse = z.infer<typeof ApiKeyResponseSchema>;

/**
 * Revoke API Key DTO Schema
 */
export const RevokeApiKeySchema = z.object({
  id: z.string().uuid(),
});

export type RevokeApiKey = z.infer<typeof RevokeApiKeySchema>;
